import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { SettingsContext } from '../contexts/SettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faTrash, faTools, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--surface-color);
  color: var(--text-color);
  padding: 2rem;
  border-radius: 1rem;
  width: 95%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-color);
  transition: color 0.3s ease;

  &:hover {
    color: var(--primary-color);
  }
`;

const Title = styled.h2`
  margin-bottom: 1.5rem;
  text-align: center;
  color: var(--primary-color);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: ${props => props.fullWidth ? 'span 2' : 'span 1'};
  
  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: var(--input-background);
  color: var(--text-color);
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: var(--input-background);
  color: var(--text-color);
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: var(--input-background);
  color: var(--text-color);
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: center;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.danger ? '#ef4444' : 'var(--primary-color)'};
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.danger ? '#dc2626' : 'var(--primary-color-dark)'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: var(--disabled-color);
    cursor: not-allowed;
    transform: none;
  }
`;

const IconPreview = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.bgColor || '#6366f1'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.5rem;
`;

const IconSelector = styled.div`
  display: flex;
  align-items: center;
`;

const SectionTitle = styled.h3`
  margin: 1.5rem 0 1rem;
  color: var(--primary-color);
  font-size: 1.1rem;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
  grid-column: span 2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  
  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const ToolsSection = styled.div`
  grid-column: span 2;
  margin-top: 1rem;
  
  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const ToolCard = styled.div`
  background-color: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;

  &:hover {
    background-color: var(--hover-color);
  }
`;

const ToolInfo = styled.div`
  flex: 1;
`;

const ToolName = styled.h4`
  margin: 0;
  font-size: 1rem;
  color: var(--text-color);
`;

const ToolDescription = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: var(--primary-color);
  }

  &:checked + span:before {
    transform: translateX(26px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

// Agent class colors
const agentColors = {
  supervisor: '#6366f1',
  strategist: '#ec4899',
  copywriter: '#f59e0b',
  seo_specialist: '#10b981',
  ppc_manager: '#3b82f6',
  social_media_manager: '#ef4444',
  data_analyst: '#8b5cf6',
  graphic_designer: '#d946ef',
  client_manager: '#14b8a6',
  image_generator: '#fbbf24'
};

// Available icons
const availableIcons = [
  'fas fa-user-tie',
  'fas fa-brain',
  'fas fa-pencil-alt',
  'fas fa-search',
  'fas fa-bullseye',
  'fas fa-hashtag',
  'fas fa-chart-line',
  'fas fa-palette',
  'fas fa-handshake',
  'fas fa-user',
  'fas fa-robot',
  'fas fa-lightbulb',
  'fas fa-rocket',
  'fas fa-star',
  'fas fa-crown',
  'fas fa-image',
  'fas fa-camera',
  'fas fa-code',
  'fas fa-database',
  'fas fa-globe'
];

// Available models
const availableModels = {
  gemini: [
    { value: 'gemini-2.0-flash-exp', label: 'Gemini 2.0 Flash (Experimental)' },
    { value: 'gemini-1.5-flash-latest', label: 'Gemini 1.5 Flash (Latest)' },
    { value: 'gemini-1.5-pro-latest', label: 'Gemini 1.5 Pro (Latest)' },
    { value: 'gemini-pro', label: 'Gemini Pro' }
  ],
  claude: [
    { value: 'claude-3-5-sonnet-20240620', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
    { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
    { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' }
  ]
};

// Available tools
const availableTools = [
  {
    id: 'google_search',
    name: 'Google Search',
    description: 'Search Google for real-time information and current events',
    icon: 'fas fa-search'
  },
  {
    id: 'code_execution',
    name: 'Code Execution',
    description: 'Execute Python or JavaScript code in a sandboxed environment',
    icon: 'fas fa-code'
  },
  {
    id: 'image_generation',
    name: 'Image Generation',
    description: 'Generate images using Gemini 2.0 Flash',
    icon: 'fas fa-image'
  },
  {
    id: 'document_processor',
    name: 'Document Processing',
    description: 'Process and analyze PDFs, images, and other documents',
    icon: 'fas fa-file-alt'
  },
  {
    id: 'url_context',
    name: 'URL Context',
    description: 'Fetch and analyze content from URLs',
    icon: 'fas fa-link'
  },
  {
    id: 'thinking_mode',
    name: 'Thinking Mode',
    description: 'Enable step-by-step reasoning before answering',
    icon: 'fas fa-brain'
  },
  {
    id: 'mcp_server',
    name: 'MCP Server',
    description: 'Connect to Model Context Protocol servers',
    icon: 'fas fa-server'
  }
];

function CharacterEditModal({ isOpen, onClose, character, isNewCharacter = false }) {
  const { updateCharacter, addCharacter, deleteCharacter, settings } = useContext(SettingsContext);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    icon: 'fas fa-user',
    role: '',
    description: '',
    goal: '',
    backstory: '',
    systemPrompt: '',
    model: settings.aiProvider === 'gemini' ? 'gemini-2.0-flash-exp' : 'claude-3-5-sonnet-20240620',
    maxTokens: 8192,
    temperature: 0.7,
    agentClass: 'custom',
    tools: [],
    thinkingMode: false,
    canGenerateImages: false
  });

  const [showTools, setShowTools] = useState(false);

  useEffect(() => {
    if (character) {
      setFormData({
        id: character.id || `agent_${Date.now()}`,
        name: character.name || '',
        icon: character.icon || 'fas fa-user',
        role: character.role || '',
        description: character.description || '',
        goal: character.goal || '',
        backstory: character.backstory || '',
        systemPrompt: character.systemPrompt || '',
        model: character.model || (settings.aiProvider === 'gemini' ? 'gemini-2.0-flash-exp' : 'claude-3-5-sonnet-20240620'),
        maxTokens: character.maxTokens || 8192,
        temperature: character.temperature || 0.7,
        agentClass: character.agentClass || 'custom',
        tools: character.tools || [],
            thinkingMode: character.thinkingMode || false,
    canGenerateImages: character.canGenerateImages || false,
    thinkingBudget: character.thinkingBudget || 0
      });
    }
  }, [character, settings.aiProvider]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked :
              name === 'maxTokens' ? parseInt(value) || 0 : 
              name === 'temperature' ? parseFloat(value) || 0 : 
              value
    }));
  };

  const handleToolToggle = (toolId) => {
    setFormData(prev => ({
      ...prev,
      tools: prev.tools.includes(toolId)
        ? prev.tools.filter(id => id !== toolId)
        : [...prev.tools, toolId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Character name is required');
      return;
    }

    const characterData = {
      ...formData,
      isSupervisor: formData.id === 'supervisor',
      thinkingMode: formData.thinkingMode,
      thinkingBudget: formData.thinkingBudget || 0
    };

    if (isNewCharacter) {
      addCharacter(characterData);
    } else {
      updateCharacter(characterData, character.id);
    }
    
    onClose();
  };

  const handleDelete = () => {
    if (formData.id === 'supervisor') {
      alert('Cannot delete the project manager');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${formData.name}?`)) {
      deleteCharacter(formData.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  const currentModels = availableModels[settings.aiProvider] || availableModels.gemini;

  return (
    <ModalBackground onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        
        <Title>
          {isNewCharacter ? 'Create New Character' : `Edit ${formData.name}`}
        </Title>
        
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormGroup>
              <Label>Name *</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Marketing Strategist"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Role</Label>
            <Input
              type="text"
                name="role"
                value={formData.role}
                onChange={handleChange}
                placeholder="e.g., Digital Marketing Expert"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Icon</Label>
              <IconSelector>
                <IconPreview bgColor={agentColors[formData.agentClass]}>
                  <FontAwesomeIcon icon={formData.icon} color="white" />
                </IconPreview>
                <Select name="icon" value={formData.icon} onChange={handleChange}>
                  {availableIcons.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </Select>
              </IconSelector>
            </FormGroup>
            
            <FormGroup>
              <Label>Agent Type</Label>
              <Select name="agentClass" value={formData.agentClass} onChange={handleChange}>
                <option value="custom">Custom</option>
                <option value="strategist">Strategist</option>
                <option value="copywriter">Copywriter</option>
                <option value="seo_specialist">SEO Specialist</option>
                <option value="ppc_manager">PPC Manager</option>
                <option value="social_media_manager">Social Media Manager</option>
                <option value="data_analyst">Data Analyst</option>
                <option value="graphic_designer">Graphic Designer</option>
                <option value="client_manager">Client Manager</option>
                <option value="image_generator">Image Generator</option>
              </Select>
            </FormGroup>
            
            <FormGroup fullWidth>
              <Label>Description</Label>
            <Input
              type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of the character's expertise"
              />
            </FormGroup>
            
            <FormGroup fullWidth>
              <Label>Goal</Label>
              <Textarea
                name="goal"
                value={formData.goal}
                onChange={handleChange}
                placeholder="What is the main goal of this character?"
              />
            </FormGroup>
            
            <FormGroup fullWidth>
              <Label>Backstory</Label>
              <Textarea
                name="backstory"
                value={formData.backstory}
                onChange={handleChange}
                placeholder="Describe the character's background, experience, and personality"
                style={{ minHeight: '120px' }}
              />
            </FormGroup>
            
            <FormGroup fullWidth>
              <Label>System Prompt</Label>
              <Textarea
                name="systemPrompt"
                value={formData.systemPrompt}
                onChange={handleChange}
                placeholder="System instructions for the character"
                style={{ minHeight: '150px' }}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>AI Model</Label>
              <Select name="model" value={formData.model} onChange={handleChange}>
                {currentModels.map(model => (
                  <option key={model.value} value={model.value}>
                    {model.label}
                  </option>
                ))}
            </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>Max Tokens</Label>
            <Input
              type="number"
                name="maxTokens"
                value={formData.maxTokens}
                onChange={handleChange}
                min="100"
                max="32000"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Temperature (0-1)</Label>
            <Input
              type="number"
                name="temperature"
                value={formData.temperature}
                onChange={handleChange}
              min="0"
              max="1"
                step="0.1"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Thinking Mode</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <ToggleSwitch>
                  <ToggleInput
                    type="checkbox"
                    name="thinkingMode"
                    checked={formData.thinkingMode}
                    onChange={handleChange}
                  />
                  <ToggleSlider />
                </ToggleSwitch>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Enable advanced reasoning with Gemini 2.5 models (requires Gemini API)
                </span>
              </div>
            </FormGroup>

            {formData.thinkingMode && (
              <FormGroup>
                <Label>Thinking Budget (0-24576 tokens)</Label>
                <Input
                  type="number"
                  name="thinkingBudget"
                  value={formData.thinkingBudget}
                  onChange={handleChange}
                  min="0"
                  max="24576"
                  placeholder="1024"
                />
                <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                  Higher values allow more detailed reasoning. 0 = dynamic thinking.
                </small>
              </FormGroup>
            )}

            <SectionTitle onClick={() => setShowTools(!showTools)}>
              <FontAwesomeIcon icon={faTools} />
              Tools & Capabilities
              <FontAwesomeIcon icon={showTools ? faChevronUp : faChevronDown} style={{ marginLeft: 'auto' }} />
            </SectionTitle>
            
            {showTools && (
              <ToolsSection>
                {availableTools.map(tool => (
                  <ToolCard key={tool.id}>
                    <ToolInfo>
                      <ToolName>
                        <FontAwesomeIcon icon={tool.icon} style={{ marginRight: '0.5rem' }} />
                        {tool.name}
                      </ToolName>
                      <ToolDescription>{tool.description}</ToolDescription>
                    </ToolInfo>
                    <ToggleSwitch>
                      <ToggleInput
                        type="checkbox"
                        checked={formData.tools.includes(tool.id)}
                        onChange={() => handleToolToggle(tool.id)}
                      />
                      <ToggleSlider />
                    </ToggleSwitch>
                  </ToolCard>
                ))}
              </ToolsSection>
            )}
          </FormGrid>
          
          <ButtonGroup>
            <Button type="submit">
              <FontAwesomeIcon icon={faSave} />
              {isNewCharacter ? 'Create Character' : 'Save Changes'}
            </Button>
            
            {!isNewCharacter && formData.id !== 'supervisor' && (
              <Button type="button" danger onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} />
                Delete Character
              </Button>
            )}
          </ButtonGroup>
        </form>
      </ModalContent>
    </ModalBackground>
  );
}

export default CharacterEditModal;
